import {
  informationFieldConfigValueSchema,
  usePatient,
  usePatientInformationFields,
  usePatientTypes,
} from "@/api/Patients";
import { deducedError } from "@/Utils/ErrorUtils";
import { DescriptionList } from "@/components/DescriptionList/DescriptionList";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Heading } from "@medoma/components/Heading/Heading";
import { Loading } from "@medoma/components/Loading/Loading";
import { Text, TextWithLineBreaks } from "@medoma/components/Text/Text";
import { Trans, useLingui } from "@lingui/react/macro";
import * as Sentry from "@sentry/react";
import { NavLink, useNavigate, useParams } from "react-router";
import { z } from "zod";
import { useState, type ReactNode } from "react";
import styles from "./PatientDailyOverview.module.scss";
import { PatientLink } from "@/components/PatientLink/PatientLink";
import { usePatientsActivityOccurrencesAndGroups } from "@/api/Activities";
import NoResults from "@/components/NoResults/NoResults";
import {
  isGroup,
  timeOfDaySchema,
  type IGroupOfActivityOccurrence,
} from "@medoma/models/activities";
import { calculateVisitTimes } from "@medoma/models/visits";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import type { IActivityOccurrence } from "@medoma/models/activities";
import { TimeSpan } from "@/components/Time/TimeSpan";
import { calculateGroupStatus } from "../../Activities/ActivitiesTable/getDataFromActivityOccurrences";
import { StatusTag } from "@/components/StatusTag/StatusTag";
import { ActivityTitle } from "@/components/ActivityTitle/ActivityTitle";
import { showStatusForActivityOccurrence } from "../../Activities/ActivitiesTable/activitiesTableUtils";
import { ActorsCell } from "../../Activities/ActivitiesTable/ActorsCell";
import { dateName } from "@/Utils/DateUtils";
import {
  formatPersonalIdentityNumber,
  patientStatusSchema,
  patientTypeDictionary,
  type IExistingPatient,
} from "@medoma/models/patients";
import { humanFormat } from "@medoma/models/date-and-time";
import EmployeeName from "@/components/EmployeeName/EmployeeName";
import { CloseButton } from "@/components/NavigationButtons/NavigationButtons";
import { useSelectedDate } from "@/Utils/useSelectedDate";
import { PlainButton } from "@medoma/components/Button/Button";
import { EditCareSummary } from "@/forms/EditCareSummary";
import { EditPlannedDischargeDateTime } from "@/forms/EditPlannedDischargeDateTime";
import { EditPrimaryResponsible } from "@/forms/EditPrimaryResponsible";
import { EditPlaceOfAssessment } from "@/forms/EditPlaceOfAssessment";
import { EditReasonForAdmission } from "@/forms/EditReasonForAdmission";

type EditableField =
  | "plannedDischargeDateTime"
  | "primaryResponsible"
  | "careSummary"
  | "placeOfAssessment"
  | "reasonForAdmission";

const EditableDescription = ({
  field,
  currentlyEditing,
  setCurrentlyEditing,
  children,
}: {
  field: EditableField;
  currentlyEditing: EditableField | null;
  setCurrentlyEditing: (next: EditableField | null) => void;
  children: ReactNode;
}) => {
  const isEditing = currentlyEditing === field;
  return (
    <DescriptionList.Description>
      <div className={styles.editableField}>
        <div className={styles.editableFieldContent}>{children}</div>
        <PlainButton
          size="small"
          weight="light"
          onClick={() => setCurrentlyEditing(isEditing ? null : field)}
        >
          {isEditing ? <Trans>Avbryt</Trans> : <Trans>Ändra</Trans>}
        </PlainButton>
      </div>
    </DescriptionList.Description>
  );
};

const ActivityGroup = ({ group }: { group: IGroupOfActivityOccurrence }) => {
  const { occurrences } = group;
  const status = calculateGroupStatus(group);

  const {
    start: parsedStart,
    end: parsedEnd,
    isAnyTimeOfDay: parsedIsAnyTimeOfDay,
  } = calculateVisitTimes(group);
  const hasWellDefinedTime = parsedStart && parsedEnd;
  const showOccurrenceStatus = showStatusForActivityOccurrence({
    group,
  });

  return (
    <NavLink
      to={`occurrence-groups/${group.id}`}
      className={styles.activityOrVisitLink}
    >
      <CategoryIcon category={group.category} size="small" />
      <div className={styles.timeSpan}>
        {hasWellDefinedTime ? (
          <TimeSpan
            timespan={
              parsedIsAnyTimeOfDay
                ? timeOfDaySchema.enum.Any
                : { start: parsedStart, end: parsedEnd }
            }
          />
        ) : (
          <Text element="span">–</Text>
        )}
      </div>
      <StatusTag status={status} variant="icon" size="small" />
      <div className={styles.activityTitle}>
        {occurrences.map((occurrence) => (
          <ActivityTitle
            key={occurrence.id}
            activityOccurrence={occurrence}
            showDescriptionAndComment={true}
            showRecurringIcon={true}
            statusTag={
              showOccurrenceStatus ? (
                <StatusTag status={occurrence.status} size="tiny" />
              ) : undefined
            }
            size="small"
          />
        ))}
      </div>
      <div className={styles.assignees}>
        <ActorsCell activityOccurrenceOrGroup={group} />
      </div>
    </NavLink>
  );
};

const PlannedDischargeSection = ({
  patient,
  currentlyEditing,
  setCurrentlyEditing,
  fieldConfig,
}: {
  patient: IExistingPatient;
  currentlyEditing: EditableField | null;
  setCurrentlyEditing: (next: EditableField | null) => void;
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  const plannedDischargeDateDisplayed = patient.plannedDischargeDateTime
    ? humanFormat(patient.plannedDischargeDateTime, {
        dateStyle: "short",
      })
    : "–";
  return (
    <>
      <DescriptionList.Term>
        <Trans>Planerad utskrivning</Trans>
      </DescriptionList.Term>
      <EditableDescription
        field="plannedDischargeDateTime"
        currentlyEditing={currentlyEditing}
        setCurrentlyEditing={setCurrentlyEditing}
      >
        {currentlyEditing === "plannedDischargeDateTime" ? (
          <EditPlannedDischargeDateTime
            currentPlannedDischargeDateTime={patient.plannedDischargeDateTime}
            onSuccess={() => setCurrentlyEditing(null)}
            patientId={patient.id}
            fieldConfig={fieldConfig}
          />
        ) : (
          plannedDischargeDateDisplayed
        )}
      </EditableDescription>
    </>
  );
};

const ReasonForAdmissionSection = ({
  patient,
  currentlyEditing,
  setCurrentlyEditing,
  fieldConfig,
}: {
  patient: IExistingPatient;
  currentlyEditing: EditableField | null;
  setCurrentlyEditing: (next: EditableField | null) => void;
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => (
  <>
    <DescriptionList.Term>
      <Trans>Inskrivningsorsak</Trans>
    </DescriptionList.Term>
    <EditableDescription
      field="reasonForAdmission"
      currentlyEditing={currentlyEditing}
      setCurrentlyEditing={setCurrentlyEditing}
    >
      {currentlyEditing === "reasonForAdmission" ? (
        <EditReasonForAdmission
          currentReasonForAdmission={patient.reasonForAdmission}
          onSuccess={() => setCurrentlyEditing(null)}
          patientId={patient.id}
          fieldConfig={fieldConfig}
        />
      ) : patient.reasonForAdmission ? (
        <TextWithLineBreaks>{patient.reasonForAdmission}</TextWithLineBreaks>
      ) : (
        "–"
      )}
    </EditableDescription>
  </>
);

const CareSummarySection = ({
  patient,
  currentlyEditing,
  setCurrentlyEditing,
  fieldConfig,
}: {
  patient: IExistingPatient;
  currentlyEditing: EditableField | null;
  setCurrentlyEditing: (next: EditableField | null) => void;
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => (
  <>
    <DescriptionList.Term>
      <Trans>Vårdsammanfattning</Trans>
    </DescriptionList.Term>
    <EditableDescription
      field="careSummary"
      currentlyEditing={currentlyEditing}
      setCurrentlyEditing={setCurrentlyEditing}
    >
      {currentlyEditing === "careSummary" ? (
        <EditCareSummary
          currentCareSummary={patient.careSummary}
          onSuccess={() => setCurrentlyEditing(null)}
          patientId={patient.id}
          fieldConfig={fieldConfig}
        />
      ) : patient.careSummary ? (
        <TextWithLineBreaks>{patient.careSummary}</TextWithLineBreaks>
      ) : (
        "–"
      )}
    </EditableDescription>
  </>
);

const ActivityOccurrence = ({
  activityOccurrence,
}: {
  activityOccurrence: IActivityOccurrence;
}) => {
  const { category, timeOfDay, start, end, status } = activityOccurrence;

  return (
    <NavLink
      to={`activities/${activityOccurrence.activityId}/occurrences/${activityOccurrence.id}`}
      className={styles.activityOrVisitLink}
    >
      <CategoryIcon category={category} size="small" />
      <div className={styles.timeSpan}>
        <TimeSpan
          timespan={
            timeOfDay === timeOfDaySchema.enum.Any ? timeOfDay : { start, end }
          }
          size="small"
        />
      </div>
      <StatusTag status={status} variant="icon" size="small" />
      <div className={styles.activityTitle}>
        <ActivityTitle
          activityOccurrence={activityOccurrence}
          showDescriptionAndComment={true}
          showRecurringIcon={true}
          size="small"
        />
      </div>
      <div className={styles.assignees}>
        <ActorsCell activityOccurrenceOrGroup={activityOccurrence} />
      </div>
    </NavLink>
  );
};

const PatientDailyOverview = () => {
  const { patientId } = z.object({ patientId: z.string() }).parse(useParams());
  return <PatientDailyOverviewContent key={patientId} patientId={patientId} />;
};

const PatientDailyOverviewContent = ({ patientId }: { patientId: string }) => {
  const dateToDisplay = useSelectedDate();
  const { t } = useLingui();
  const { data: patient, isPending, isError, error } = usePatient(patientId);
  const {
    data: activityOccurrencesAndGroups,
    isPending: isActivitiesPending,
    isError: isActivitiesError,
    error: activitiesError,
  } = usePatientsActivityOccurrencesAndGroups(
    patientId,
    dateToDisplay,
    dateToDisplay,
  );
  const {
    data: fieldsConfig,
    isPending: isPendingFieldsConfig,
    isError: isErrorFieldsConfig,
    error: fieldsConfigError,
  } = usePatientInformationFields();
  const { data: patientTypes } = usePatientTypes();
  const navigate = useNavigate();
  const [currentlyEditing, setCurrentlyEditing] =
    useState<EditableField | null>(null);

  if (isPending || isActivitiesPending || isPendingFieldsConfig) {
    return <Loading message={t`Hämtar patientinformation`} padding={24} />;
  }

  if (isError || isActivitiesError || isErrorFieldsConfig) {
    Sentry.captureException(error || activitiesError || fieldsConfigError);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta patientinformation.`} ${deducedError(error || activitiesError || fieldsConfigError)}`}
        padding={24}
      />
    );
  }

  const showReasonForAdmission =
    fieldsConfig.ReasonForAdmission !==
    informationFieldConfigValueSchema.enum.hidden;

  const displayedDateName = dateName(dateToDisplay);
  const admittedAtDisplayedDate =
    patient.status === patientStatusSchema.enum.admitted
      ? humanFormat(patient.admittedAt, {
          dateStyle: "short",
        })
      : "–";
  const registeredAtDisplayedDate =
    patient.status === patientStatusSchema.enum.prospect
      ? humanFormat(patient.registeredAt, {
          dateStyle: "short",
        })
      : "–";

  return (
    <article className={styles.patientDailyOverview}>
      <div className={styles.patientHeader}>
        <div className={styles.nameAndPIN}>
          <h2>
            <PatientLink patient={patient} size="large" weight="bold" />
          </h2>
          <Text element="span" size="large">
            {formatPersonalIdentityNumber({
              personalIdentityNumber: patient.personalIdentityNumber,
              personalIdentityNumberType: patient.personalIdentityNumberType,
            })}
          </Text>
        </div>
        <CloseButton close={() => navigate("..")} />
      </div>

      <DescriptionList>
        {patientTypes && patientTypes.length > 1 ? (
          <>
            <DescriptionList.Term>
              <Trans>Patienttyp</Trans>
            </DescriptionList.Term>
            <DescriptionList.Description>
              {t(patientTypeDictionary[patient.patientType])}
            </DescriptionList.Description>
          </>
        ) : null}
        {patient.status === patientStatusSchema.enum.prospect ? (
          <>
            <DescriptionList.Term>
              <Trans>Registrerad</Trans>
            </DescriptionList.Term>
            <DescriptionList.Description>
              {registeredAtDisplayedDate}
            </DescriptionList.Description>

            <PlannedDischargeSection
              patient={patient}
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              fieldConfig={fieldsConfig.PlannedDischargeDateTime}
            />

            <DescriptionList.Term>
              <Trans>Plats för bedömning</Trans>
            </DescriptionList.Term>
            <EditableDescription
              field="placeOfAssessment"
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
            >
              {currentlyEditing === "placeOfAssessment" ? (
                <EditPlaceOfAssessment
                  currentPlaceOfAssessment={patient.placeOfAssessment}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldsConfig.PlaceOfAssessment}
                />
              ) : (
                (patient.placeOfAssessment ?? "–")
              )}
            </EditableDescription>

            {showReasonForAdmission ? (
              <ReasonForAdmissionSection
                patient={patient}
                currentlyEditing={currentlyEditing}
                setCurrentlyEditing={setCurrentlyEditing}
                fieldConfig={fieldsConfig.ReasonForAdmission}
              />
            ) : null}

            <CareSummarySection
              patient={patient}
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              fieldConfig={fieldsConfig.CareSummary}
            />
          </>
        ) : (
          <>
            <DescriptionList.Term>
              <Trans>Inskriven</Trans>
            </DescriptionList.Term>
            <DescriptionList.Description>
              {admittedAtDisplayedDate}
            </DescriptionList.Description>

            <PlannedDischargeSection
              patient={patient}
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              fieldConfig={fieldsConfig.PlannedDischargeDateTime}
            />

            <DescriptionList.Term>
              <Trans>Patientansvarig</Trans>
            </DescriptionList.Term>
            <EditableDescription
              field="primaryResponsible"
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
            >
              {currentlyEditing === "primaryResponsible" ? (
                <EditPrimaryResponsible
                  currentPrimaryResponsible={patient.primaryResponsible}
                  onSuccess={() => setCurrentlyEditing(null)}
                  patientId={patient.id}
                  fieldConfig={fieldsConfig.PrimaryResponsible}
                />
              ) : patient.primaryResponsible ? (
                <EmployeeName
                  employeeId={patient.primaryResponsible}
                  showCompetence={true}
                />
              ) : (
                "–"
              )}
            </EditableDescription>

            {showReasonForAdmission ? (
              <ReasonForAdmissionSection
                patient={patient}
                currentlyEditing={currentlyEditing}
                setCurrentlyEditing={setCurrentlyEditing}
                fieldConfig={fieldsConfig.ReasonForAdmission}
              />
            ) : null}

            <CareSummarySection
              patient={patient}
              currentlyEditing={currentlyEditing}
              setCurrentlyEditing={setCurrentlyEditing}
              fieldConfig={fieldsConfig.CareSummary}
            />
          </>
        )}
      </DescriptionList>

      <section className={styles.activities}>
        <Heading level="h2" size="base">
          <Trans>Aktiviteter ({displayedDateName})</Trans>
        </Heading>
        {activityOccurrencesAndGroups.length === 0 ? (
          <NoResults message={t`Inga aktiviteter denna dag`} />
        ) : (
          <ul className={styles.activitiesList}>
            {activityOccurrencesAndGroups.map((activityOccurrenceOrGroup) => {
              if (isGroup(activityOccurrenceOrGroup)) {
                const group = activityOccurrenceOrGroup;

                return (
                  <li key={group.id} className={styles.activityRow}>
                    <ActivityGroup group={group} />
                  </li>
                );
              }

              const activityOccurrence = activityOccurrenceOrGroup;

              return (
                <li
                  key={`${activityOccurrence.activityId}-${activityOccurrence.id}`}
                  className={styles.activityRow}
                >
                  <ActivityOccurrence activityOccurrence={activityOccurrence} />
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </article>
  );
};

export { PatientDailyOverview };
